import { memo, useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { QuestionTooltip } from '@app/core';
import { Criterion, Field, FieldType, Quota } from 'queries';
import { FieldConfig, useFieldConfigurations } from '../fieldConfigurations';
import { useTranslation } from 'react-i18next';
import { LogicJumpOption } from './elements/interface';
import { LogicDefaultJumpSelector } from './elements/LogicDefaultJumpSelector';
import { updateLogicJumpWithDefaultAction } from 'components/CustomForm/lib';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    marginTop: theme.marginBase * 3,
    borderRadius: theme.borderRadius.std,
    '&:hover': {
      boxShadow: theme.boxShadow.base,
    },
    [theme.media.tablet]: {
      marginTop: 0,
      marginBottom: theme.marginBase * 2,
    },
  },
  defaultLogicJumpSelectorContainer: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

interface Props {
  field: Field;
  onChange: (field: Field) => any;
  fieldNumber?: number;
  criterion?: Criterion;
  onCriterionChange?: (criterion: Criterion) => any;
  logicJumpOptions?: LogicJumpOption[];
  isLastField?: boolean;
  shouldAlwaysValidate?: boolean;
  planId?: string;
  numberOfParticipantRequired?: number;
  sessionId?: string;
  quotas?: Quota[];
}

export const FieldPreview = memo(
  ({
    field,
    onChange,
    fieldNumber,
    isLastField,
    onCriterionChange,
    criterion,
    shouldAlwaysValidate,
    planId,
    logicJumpOptions,
    numberOfParticipantRequired,
    sessionId,
    quotas,
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });
    const { t } = useTranslation();

    const fieldConfigurations = useFieldConfigurations();
    const config = fieldConfigurations[field.type] as FieldConfig<typeof field>;

    const onDefaultLogicJumpSelect = useCallback(
      (logicOption: LogicJumpOption) => {
        const updatedActions = updateLogicJumpWithDefaultAction(
          field.id,
          logicOption,
          field.jumpActions,
        );

        onChange({
          ...field,
          jumpActions: updatedActions,
        });
      },
      [field, onChange],
    );

    const defaultJump = useMemo(
      () => field.jumpActions?.find((j) => !j.conditionChoiceRefs),
      [field.jumpActions],
    );

    return (
      <div className={classes.container}>
        {config.renderPreview({
          field,
          onChange,
          fieldNumber,
          logicJumpOptions,
          criterion,
          onCriterionChange,
          mustChooseValidation: shouldAlwaysValidate,
          numberOfParticipantRequired,
          sessionId,
          quotas,
        } as never)}
        <div className={classes.defaultLogicJumpSelectorContainer}>
          <LogicDefaultJumpSelector
            onChange={onDefaultLogicJumpSelect}
            value={defaultJump}
            logicJumpOptions={logicJumpOptions}
            planId={planId}
            isLastField={isLastField}
          />

          {field.type === FieldType.MULTIPLE_CHOICE && (
            <div>
              <QuestionTooltip
                title={t(
                  'screeningForm.create.questionBuilder.logicHelp.tooltip',
                )}
                variant="gray"
              >
                {t('screeningForm.create.questionBuilder.logicHelp.text')}
              </QuestionTooltip>
            </div>
          )}
        </div>
      </div>
    );
  },
);
